// deriveWorkerContext — the context of an ephemeral worker a DE spawns (05 Delegation rule 8).
// A worker is a delegation at depth + 1 under rules 3–6: scoped context refs, a budget reserved
// from the DE's, a deadline, no inherited access. The only thing copied in is the DE's standing
// orders, verbatim, from the deployed bundle (template version plus company override). The lead
// profile's instructions, skill index, memory scope and agent key are never carried over: the
// result is built field by field from strictly parsed input, not by spreading the lead.
//
// The bundle is untrusted at this point (it was loaded from storage): its file hashes and
// bundle hash are recomputed and compared with `trustedBundleSha`, the hash recorded on the
// deploy row, before the standing orders are read.
import { z } from "zod";
import type { ConfigurationBundle } from "./bundle";
import { verifyBundleIntegrity } from "./bundle-checks";
import { type DeployError, err, fromZod, type Result, untrustedInputErrors } from "./errors";

/** Delegation rule 6 default `policy.max_depth`; callers pass their policy explicitly. */
export const DEFAULT_MAX_DELEGATION_DEPTH = 2;

const count = z.int().min(0);

/** What the lead run knows about itself. Only these fields are read; others are dropped. */
export const leadRunStateSchema = z.object({
  depth: count,
  remainingBudgetMicros: count,
});
export type LeadRunState = z.input<typeof leadRunStateSchema>;

export const workerRequestSchema = z.strictObject({
  contextRefs: z.array(z.string().min(1)),
  budgetMicros: count,
  /** ISO-8601 deadline with offset; required (rule 5). */
  deadline: z.iso.datetime({ offset: true }),
});
export type WorkerRequest = z.input<typeof workerRequestSchema>;

/** Trusted delegation policy (rule 6 `policy.max_depth`). */
export const delegationPolicySchema = z.strictObject({ maxDepth: count });
export type DelegationPolicy = z.input<typeof delegationPolicySchema>;

/** Every field a worker context has. Nothing else may appear on one. */
export const WORKER_CONTEXT_FIELDS = [
  "standingOrders",
  "contextRefs",
  "budgetMicros",
  "deadline",
  "depth",
  "bundleSha",
] as const;

export const workerContextSchema = z.strictObject({
  standingOrders: z.string(),
  contextRefs: z.array(z.string().min(1)),
  budgetMicros: count,
  deadline: z.iso.datetime({ offset: true }),
  depth: count,
  bundleSha: z.string().regex(/^[0-9a-f]{64}$/),
});
export type WorkerContext = Readonly<z.infer<typeof workerContextSchema>>;

/**
 * What the worker rules read of a bundle: its files (with their hashes) and its hash. A bundle
 * loaded back from storage has no manifest object of its own (DC-08: the API's admission).
 */
export type WorkerBundle = Pick<ConfigurationBundle, "bundleSha" | "files">;

/**
 * The worker's instructions: the standing orders, then (TEMPLATES-T3) the bundle's `overlay.md`
 * (the vertical pack's and the company's instruction blocks, headed by layer and version), so a
 * worker carries the company's restrictions as its lead does.
 */
function standingOrdersOf(bundle: WorkerBundle): string | null {
  const orders = bundle.files.find((f) => f.path === "standing-orders.md")?.content ?? null;
  if (orders === null) return null;
  const overlay = bundle.files.find((f) => f.path === "overlay.md")?.content;
  return overlay?.trim() ? `${orders.trimEnd()}\n\n${overlay}` : orders;
}

function parse<T>(schema: z.ZodType<T>, value: unknown, path: string): Result<T> {
  const unsafe = untrustedInputErrors(value, path);
  if (unsafe.length > 0) return { ok: false, errors: unsafe };
  const r = schema.safeParse(value);
  return r.success
    ? { ok: true, value: r.data }
    : { ok: false, errors: fromZod(r.error, "worker_request_invalid", path) };
}

export type DeriveWorkerInput = {
  bundle: WorkerBundle;
  /** The deploy row's `bundle_sha`; never taken from the bundle itself. */
  trustedBundleSha: string;
  lead: unknown;
  request: unknown;
  policy: unknown;
};

export function deriveWorkerContext(input: DeriveWorkerInput): Result<WorkerContext> {
  const integrity = verifyBundleIntegrity(input.bundle, input.trustedBundleSha);
  if (integrity.length > 0) return { ok: false, errors: integrity };
  const orders = standingOrdersOf(input.bundle);
  if (orders === null)
    return {
      ok: false,
      errors: [
        err("standing_orders_missing", "standing-orders.md", "bundle has no standing orders"),
      ],
    };

  const lead = parse(leadRunStateSchema, input.lead, "lead");
  const request = parse(workerRequestSchema, input.request, "request");
  const policy = parse(delegationPolicySchema, input.policy, "policy");
  const parseErrors = [lead, request, policy].flatMap((r) => (r.ok ? [] : r.errors));
  if (!lead.ok || !request.ok || !policy.ok) return { ok: false, errors: parseErrors };

  const errors: DeployError[] = [];
  const depth = lead.value.depth + 1;
  if (depth > policy.value.maxDepth)
    errors.push(
      err("worker_depth_exceeded", "depth", `depth ${depth} exceeds max ${policy.value.maxDepth}`),
    );
  if (request.value.budgetMicros > lead.value.remainingBudgetMicros)
    errors.push(
      err(
        "worker_budget_exceeds_remaining",
        "request.budgetMicros",
        `budget ${request.value.budgetMicros} exceeds the DE's remaining ${lead.value.remainingBudgetMicros}`,
      ),
    );
  if (errors.length > 0) return { ok: false, errors };

  const context: WorkerContext = Object.freeze({
    standingOrders: orders,
    contextRefs: Object.freeze([...new Set(request.value.contextRefs)].sort()) as string[],
    budgetMicros: request.value.budgetMicros,
    deadline: request.value.deadline,
    depth,
    bundleSha: input.trustedBundleSha,
  });
  return { ok: true, value: context };
}

/**
 * Admission check (DC-08): the bundle must match the trusted deployed hash, and the worker's
 * context must parse strictly and carry standing orders byte-identical to the bundle's.
 */
export function admitWorker(input: {
  context: unknown;
  bundle: WorkerBundle;
  trustedBundleSha: string;
}): Result<true> {
  const integrity = verifyBundleIntegrity(input.bundle, input.trustedBundleSha);
  if (integrity.length > 0) return { ok: false, errors: integrity };
  const ctx = parse(workerContextSchema, input.context, "context");
  if (!ctx.ok) return ctx;
  const mismatch = (): Result<true> => ({
    ok: false,
    errors: [
      err(
        "worker_standing_orders_mismatch",
        "standingOrders",
        "worker standing orders are missing or differ from the deployed bundle",
      ),
    ],
  });
  if (ctx.value.bundleSha !== input.trustedBundleSha) return mismatch();
  if (ctx.value.standingOrders !== standingOrdersOf(input.bundle)) return mismatch();
  return { ok: true, value: true };
}
